/**
 * What a document is called, everywhere it is named.
 *
 * The Downloads hub, the notification that announces a purchase and the
 * watchlist feed all label the same documents, so the names live here rather
 * than in whichever component happened to need them first — a document
 * called "Gesellschafterliste" in one place and "DK" in another reads as two
 * different things.
 *
 * Full names only: no "(AD)" / "(DK)" shorthand. It means nothing outside
 * the company page's dense sidebar.
 */

export const FILE_TYPE_LABELS: Record<string, string> = {
  Bilanz: 'Jahresabschluss',
  HandelsRegisterAD: 'Aktueller Abdruck',
  HandelsRegisterCD: 'Chronologischer Abdruck',
  HandelsRegisterSI: 'Strukturierte Inhalte',
  HandelsRegisterDKGesellschafter: 'Gesellschafterliste',
  HandelsRegisterDKMusterProtokol: 'Musterprotokoll',
  HandelsRegisterDKSatzung: 'Satzung',
  HandelsRegisterDKAnmeldung: 'Anmeldung',
};

/** Register documents are served as PDFs; a Bilanz is rendered HTML. */
export const PDF_FILE_TYPES = new Set([
  'HandelsRegisterAD',
  'HandelsRegisterCD',
  'HandelsRegisterSI',
  'HandelsRegisterDKGesellschafter',
  'HandelsRegisterDKMusterProtokol',
  'HandelsRegisterDKSatzung',
  'HandelsRegisterDKAnmeldung',
]);

export function typeLabel(fileType: string): string {
  return FILE_TYPE_LABELS[fileType] ?? fileType;
}

/** A filename for a document that never carried one of its own. */
export function derivedFileName(fileType: string, fallback: string): string {
  const label = FILE_TYPE_LABELS[fileType];
  if (!label) return fileType || fallback;
  return PDF_FILE_TYPES.has(fileType) ? `${label}.pdf` : label;
}

/**
 * Label a document from its resource key alone.
 *
 * Notifications carry the key, not the file type, because the key is the
 * identity shared with billing. `regfile:<eu_id>:<type>` names its type
 * directly; publication keys (`bilanz_view:<file_id>`) only say what kind of
 * thing they are.
 */
export function documentLabelFromKey(key: string): string | null {
  if (key.startsWith('regdoc:')) {
    const rest = key.slice('regdoc:'.length);
    const idCut = rest.lastIndexOf(':');
    if (idCut <= 0) return null;
    const typeCut = rest.lastIndexOf(':', idCut - 1);
    if (typeCut <= 0) return null;
    return typeLabel(rest.slice(typeCut + 1, idCut));
  }
  if (key.startsWith('regfile:')) {
    const rest = key.slice('regfile:'.length);
    // eu_ids contain dots but no colons — the type is the trailing field.
    const cut = rest.lastIndexOf(':');
    if (cut > 0) return typeLabel(rest.slice(cut + 1));
    return null;
  }
  if (
    key.startsWith('bilanz_view:') ||
    key.startsWith('bilanz_html_download:')
  ) {
    return FILE_TYPE_LABELS.Bilanz;
  }
  if (key.startsWith('dk:')) {
    return FILE_TYPE_LABELS.HandelsRegisterDKGesellschafter;
  }
  return null;
}
